import { Box, Typography, useTheme, Card } from "@mui/material";
import { tokens } from "../../theme";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TrafficIcon from "@mui/icons-material/Traffic";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import AuthGuard from "../../components/AuthGuard";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { jwtDecode } from "jwt-decode";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { green, red } from "@mui/material/colors";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import StatCards from "../../components/StatCard";
import BeatLoader from "react-spinners/BeatLoader";

const override = {
  display: "block",
  margin: "0 auto",
  position: "relative",
  bottom: "45%",
  left: "30%",
};

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dashboardData, setDashboardData] = useState({});
  const [cookies, setCookie] = useCookies(["token"]);
  const navigate = useNavigate();
  const [jsonData, setJsonData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchDashboard = async () => {
    if (!cookies["token"]) {
      navigate("/login");
      return;
    }
    const decoded = jwtDecode(cookies["token"]);
    const userData = JSON.parse(decoded.userInfo);
    setJsonData(userData);
    let response;
    try {
      response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/dashboard/getData/`,
        {
          headers: {
            Authorization: `Bearer ${cookies["token"]}`,
            "Content-Type": "application/json",
          },
        },
      );
    } catch (err) {
      console.error("Dashboard fetch failed (network/timeout error):", err);
      return;
    }
    if (response.status === 401 || response.status === 403) {
      console.error(
        `Dashboard fetch unauthorized (status ${response.status}), logging out.`,
      );
      setCookie("token", null);
      navigate("/login");
      return;
    }
    if (response.status !== 200) {
      const body = await response.text().catch(() => "<unreadable body>");
      console.error(
        `Dashboard fetch failed with status ${response.status}:`,
        body,
      );
      return;
    }
    const data = await response.json();
    setDashboardData(data);
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    let interval;
    if (
      dashboardData.currentBalance &&
      dashboardData.currentBalance.length &&
      dashboardData.accountNumbers &&
      dashboardData.accountNumbers.length &&
      dashboardData.currentBalance.length ===
        dashboardData.accountNumbers.length
    ) {
      interval = setInterval(() => {
        setCurrentIndex(
          (prevIndex) => (prevIndex + 1) % dashboardData.currentBalance.length,
        );
      }, 5000);
    }

    // Cleanup interval on component unmount or when dashboardData changes
    return () => clearInterval(interval);
  }, [dashboardData.currentBalance, dashboardData.accountNumbers]);

  if (!dashboardData || Object.keys(dashboardData).length === 0) {
    return (
      <Box m="120px">
        <BeatLoader
          color={"#198484"}
          loading={true}
          cssOverride={override}
          size={30}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </Box>
    );
  }

  return (
    <AuthGuard>
      <Box m="10px">
        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header subtitle="Welcome to Zamzam Bank ZamZam Online System" />
        </Box>

        {/* GRID & CHARTS */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridAutoRows="140px"
          gap="20px"
          marginTop="4%"
        >
          {/* ROW 1 */}
          {jsonData &&
            (jsonData.roles.name === "Company User" ||
              jsonData.roles.name === "Company Admin") &&
            dashboardData.currentBalance && (
              <Box
                gridColumn="span 3"
                backgroundColor="#fff"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <StatCards
                  title={`Balance For Today  ${dashboardData.accountNumbers[currentIndex]}`}
                  icon={
                    <AttachMoneyIcon sx={{ color: "#fff", fontSize: "26px" }} />
                  }
                  value={`${dashboardData.currentBalance[currentIndex]} ETB`}
                />
              </Box>
            )}
          {jsonData &&
            jsonData.roles.name !== "Company User" &&
            jsonData.roles.name !== "Company Admin" && (
              <Box
                gridColumn="span 3"
                backgroundColor="#fff"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <StatCards
                  title={"Companies"}
                  icon={
                    <DomainDisabledIcon
                      sx={{ color: "#fff", fontSize: "26px" }}
                    />
                  }
                  value={dashboardData.companies}
                />
              </Box>
            )}
          <Box
            gridColumn="span 3"
            backgroundColor="#fff"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <StatCards
              title={"Company Users"}
              icon={<PersonAddIcon sx={{ color: "#fff", fontSize: "26px" }} />}
              value={dashboardData.customers}
            />
          </Box>
          {jsonData && jsonData.roles.name !== "Company User" && (
            <Box
              gridColumn="span 3"
              backgroundColor="#fff"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <StatCards
                title={"Company Admin"}
                icon={
                  <PersonAddIcon sx={{ color: "#fff", fontSize: "26px" }} />
                }
                value={dashboardData.company_admin}
              />
            </Box>
          )}
          <Box
            gridColumn="span 3"
            backgroundColor="#fff"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <StatCards
              title={"Accounts"}
              icon={
                <AccountBalanceWalletIcon
                  sx={{ color: "#fff", fontSize: "26px" }}
                />
              }
              value={dashboardData.accounts}
            />
          </Box>
          {jsonData && jsonData.roles.name === "Super Admin" && (
            <Box
              gridColumn="span 3"
              backgroundColor="#fff"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <StatCards
                title={"Admin Users"}
                icon={<TrafficIcon sx={{ color: "#fff", fontSize: "26px" }} />}
                value={dashboardData.admins}
              />
            </Box>
          )}
          {/* ROW 2 */}
          {jsonData &&
            (jsonData.roles.name === "Company User" ||
              jsonData.roles.name === "Company Admin") && (
              <Box gridColumn="span 12" gridRow="span 2">
                <Card sx={{ backgroundColor: "#fff" }}>
                  <Box
                    mt="25px"
                    p="0 30px"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={"100%"}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="600"
                        color={colors.black[100]}
                      >
                        Transaction Graph
                      </Typography>
                    </Box>
                  </Box>
                  <Box height="250px" m="-20px 0 0 0">
                    <LineChart isDashboard={true} data={dashboardData} />
                  </Box>
                </Card>
              </Box>
            )}
          {jsonData &&
            (jsonData.roles.name === "Company User" ||
              jsonData.roles.name === "Company Admin") && (
              <Box gridColumn="span 6" gridRow="span 3" overflow="auto">
                <Card sx={{ backgroundColor: "#fff" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom={`4px solid ${colors.zamzamBrandGreen[500]}`}
                    colors={colors.black[100]}
                    p="15px"
                  >
                    <Typography
                      color={colors.zamzamBrandGrey[500]}
                      variant="h5"
                      fontWeight="600"
                    >
                      Recent Transactions
                    </Typography>
                  </Box>
                  {dashboardData.latestTrns &&
                    dashboardData.latestTrns.map((transaction, i) => (
                      <Box
                        key={`${transaction.sequence}-${i}`}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        borderBottom={`4px solid ${colors.black[500]}`}
                        p="15px"
                      >
                        <Box
                          gridColumn="1"
                          color={colors.black[500]}
                          p="5px 10px"
                          borderRadius="4px"
                        >
                          {transaction.DEBIT == null
                            ? transaction.CREDIT
                            : transaction.DEBIT}
                        </Box>
                        <Box
                          gridColumn="1 !important"
                          color={colors.black[500]}
                          p="5px 10px"
                          borderRadius="4px"
                        >
                          {transaction.DEBIT == null ? (
                            <ArrowUpwardIcon style={{ color: green[500] }} />
                          ) : (
                            <ArrowDownwardIcon style={{ color: red[500] }} />
                          )}
                        </Box>
                        <Box gridColumn="1" color={colors.black[500]}>
                          {transaction.TRN_DT}
                        </Box>
                        <Box gridColumn="2 !important">
                          <Typography color={colors.black[500]}>
                            {transaction.REFERENCE}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Card>
              </Box>
            )}
        </Box>
      </Box>
    </AuthGuard>
  );
};

export default Dashboard;
